import { format } from "libphonenumber-js";
import { isPossiblePhoneNumber, parsePhoneNumber } from "libphonenumber-js/max";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getContractAsync, postMemberContractManageInviteAsync } from "src/api/contract/contract-api";
import { ContractModel } from "src/api/contract/contract-types";
import { MediaFile } from "src/api/file/file-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { BaseButton, BaseTextInputWithButton, ConfirmModal } from "src/components";
import BaseFullModal from "src/components/BaseFullModal";
import { BaseRadio } from "src/components/layout/BaseRadio";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useMediaQueries } from "src/pages/hooks/media-query";
import ExcelButton from "src/pages/mypage/contract/components/ExcelButton";
import * as xlsx from "xlsx";
import { InviteResult } from "../user-types";
import InviteLoadingModal from "./components/InviteLoadingModal";

type InviteUserFormData = {
  phoneNumber: string;
  email: string;
  inviteUserList: Array<{ phoneNumber?: string; email?: string }>;
};

interface SheetData {
  [key: string]: string;
}

type Modal = {
  isOpen: boolean;
  type?: "INVITE_FAIL_MAX_COUNT" | "INVITE_SUCCESS" | "INVITE_FAIL" | "FILE_UPLOAD" | "CHECK_B_COLUMN" | "NO_EXCEL_DATA" | "OVER_ROW";
};

/** 엑셀 양식의 데이터 시작 행. 1~7행은 안내 영역이다 (엑셀 행 번호, 1-based) */
const EXCEL_FIRST_DATA_ROW = 8;

/** 한 번에 올릴 수 있는 이용자 수 */
const MAX_UPLOAD_ROWS = 1_000;

/** 데이터 마지막 행. 이 아래에 값이 있으면 상한을 넘긴 파일이다 */
const EXCEL_LAST_DATA_ROW = EXCEL_FIRST_DATA_ROW + MAX_UPLOAD_ROWS - 1;

/**
 * 요청 제한시간. **순차 루프에서는 한 건이 매달리면 뒤가 전부 멈춘다** — axios 기본값은 무제한
 * 대기라 로딩 모달이 영원히 돌게 된다. 타임아웃이 나면 그 건만 실패로 접고 다음 건으로 넘어간다.
 */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 이용자 목록 > 이용자 초대
 */
const UserInvite = () => {
  const { t } = useTranslation(["user", "common"]);
  const { isMobile: is_Mobile } = useMediaQueries();
  const navigate = usePartnerNavigate();

  // path variable 계약 id
  const { contractId } = useParams();

  // 신청/계약 상세 조회 api
  const { executeAsync: getContract } = useApiOperation(getContractAsync);

  // 이용자 초대 api
  const { executeAsync: postMemberContractManageInvite } = useApiOperation(postMemberContractManageInviteAsync, {
    noHandleError: true,
    timeout: REQUEST_TIMEOUT_MS,
  });

  // 신청/계약 상세
  const [contract, setContract] = useState<ContractModel | null>(null);

  // 확인 버튼만 있는 알림 모달
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  // 확인 취소 버튼 있는 확인 모달
  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false });

  // 파일(엑셀) 업로드 모달
  const [inviteLoadingModal, setInviteLoadingModal] = useState<Modal>({ isOpen: false });

  // 이용자 목록 초대 중 여부
  const [isLoadingInviteUsers, setIsLoadingInviteUsers] = useState<boolean>(false);
  const [medias, setMedias] = useState<MediaFile[]>([]);
  // 이용자 초대 결과
  const [inviteResults, setInviteResults] = useState<InviteResult[]>([]);
  const [fileUploadFullModal, setFileUploadFullModal] = useState<Modal>({ isOpen: false });

  // 라디오 버튼 상태 (1: 휴대폰번호, 2: 이메일주소)
  const [selectedType, setSelectedType] = useState<"1" | "2">("1");

  const defaultValues = useMemo(() => {
    return {
      phoneNumber: "",
      email: "",
      inviteUserList: [],
    };
  }, []);

  const { handleSubmit, register, control, setError, resetField } = useForm<InviteUserFormData>({
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    register("phoneNumber", {
      validate: {
        validatePhoneNumber: (phoneNumber: string) => {
          if (selectedType === "1" && phoneNumber && phoneNumber.trim().length > 0) {
            return isPossiblePhoneNumber(phoneNumber, "KR") || t("inviteUser.phoneValidationError");
          }
          return true;
        },
      },
    });

    register("email", {
      validate: {
        validateEmail: (email: string) => {
          if (selectedType === "2" && email && email.trim().length > 0) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email) || t("inviteUser.emailValidationError");
          }
          return true;
        },
      },
    });
  }, [register, selectedType, t]);

  // 신청/계약 상세 조회
  const fetchContract = useCallback(
    async (contractId: string) => {
      const { data } = await getContract({ contractId });
      setContract(data?.data || null);
    },
    [getContract],
  );

  useEffect(() => {
    if (contractId) {
      fetchContract(contractId);
    }
  }, [contractId, fetchContract]);

  const {
    fields: inviteUserList,
    append: appendInviteUser,
    remove: removeInviteUser,
    replace: replaceInviteUser,
  } = useFieldArray({
    control,
    name: "inviteUserList",
  });

  // 초대하기 버튼 클릭
  const clickUserInvite = useCallback(() => {
    if (Number(contract?.memberMaxNums || 0) < Number(contract?.memberTotCount || 0) + inviteUserList.length) {
      // 최대 이용자수 초과시 alert
      setAlertModal({ isOpen: true, type: "INVITE_FAIL_MAX_COUNT" });
      return;
    }
    setConfirmModal({ isOpen: true });
  }, [contract, inviteUserList]);

  // 이용자 초대
  const memberContractManageInvite = useCallback(
    async (contactInfo: string) => {
      if (!contract?.contractManageId) throw Error("contractManageId is not found");

      try {
        // 이메일인지 휴대폰번호인지 확인
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmail = emailRegex.test(contactInfo);

        if (isEmail) {
          // 이메일인 경우
          const { status, data } = await postMemberContractManageInvite({
            contractManageId: Number(contract.contractManageId),
            contractMemberList: [{ inviteEmail: contactInfo }],
          });

          if (status === 200 && !data?.meta?.errorCode) {
            const _inviteResult: InviteResult = {
              type: "SUCCESS",
              email: contactInfo,
            };

            return _inviteResult;
          } else {
            const _inviteResult: InviteResult = {
              type: "FAIL",
              email: contactInfo,
              failErrorCode: data.meta.errorCode,
            };

            return _inviteResult;
          }
        } else {
          // 휴대폰번호인 경우
          const parsedPhoneNumber = parsePhoneNumber(format(contactInfo, "KR", "NATIONAL"), "KR");

          if (!isPossiblePhoneNumber(contactInfo, "KR") || parsedPhoneNumber.getType() !== "MOBILE") {
            const _inviteResult: InviteResult = {
              type: "FAIL",
              phoneNumber: contactInfo,
            };

            return _inviteResult;
          }

          const { status, data } = await postMemberContractManageInvite({
            contractManageId: Number(contract.contractManageId),
            contractMemberList: [{ inviteMobileNumber: contactInfo }],
          });

          if (status === 200 && !data?.meta?.errorCode) {
            const _inviteResult: InviteResult = {
              type: "SUCCESS",
              phoneNumber: contactInfo,
            };

            return _inviteResult;
          } else {
            const _inviteResult: InviteResult = {
              type: "FAIL",
              phoneNumber: contactInfo,
              failErrorCode: data.meta.errorCode,
            };

            return _inviteResult;
          }
        }
      } catch (error: any) {
        const _inviteResult: InviteResult = {
          type: "FAIL",
          phoneNumber: contactInfo,
          failErrorCode: error?.response?.data?.meta?.errorCode,
        };

        return _inviteResult;
      }
    },
    [contract, postMemberContractManageInvite],
  );

  /**
   * 순차 초대 루프의 취소 신호.
   *
   * 상한이 1000건이라 루프가 몇 분씩 돈다. 그 사이 화면을 떠나면 남은 요청과 setState 가
   * 계속 나가므로, 시작할 때마다 새 컨트롤러를 만들고 언마운트에서 끊는다.
   */
  const workRef = useRef<AbortController | null>(null);

  const startWork = useCallback(() => {
    workRef.current?.abort();

    const controller = new AbortController();
    workRef.current = controller;

    return controller.signal;
  }, []);

  useEffect(() => {
    return () => {
      workRef.current?.abort();
    };
  }, []);

  // 확인 모달 확인 버튼 클릭
  const clickModalConfirm = useCallback(async () => {
    setConfirmModal({ isOpen: false });

    // 초대하기 버튼이 비어 있으면 눌리지 않지만, 여기서 throw 하면 미처리 거부로 새고 로딩 모달이 남는다
    if (inviteUserList.length === 0 || !contract?.contractManageId) {
      return;
    }

    setInviteLoadingModal({ isOpen: true });
    setIsLoadingInviteUsers(true);

    const signal = startWork();
    const results: InviteResult[] = [];

    // **한 건의 회신을 받고 다음 건을 보낸다.** 한꺼번에 쏘면 건수만큼 요청이 서버에 몰린다
    for (const inviteUser of inviteUserList) {
      if (signal.aborted) {
        return;
      }

      const contactInfo = inviteUser.phoneNumber || inviteUser.email || "";
      // 실패는 throw 가 아니라 type: "FAIL" 로 돌아온다. 한 건이 실패해도 루프는 계속 간다
      const result = await memberContractManageInvite(contactInfo);

      if (signal.aborted) {
        return;
      }

      results.push(result);
      // 진행률(current/total)은 이 배열 길이를 본다
      setInviteResults((previous) => [...previous, result]);
    }

    setInviteLoadingModal({ isOpen: false });
    setIsLoadingInviteUsers(false);

    // 초대완료 후 띄우는 알림 모달 (실패 건수 있을 경우 or 없을 경우)
    const hasFail = results.some((result) => result.type === "FAIL");
    setAlertModal({ isOpen: true, type: hasFail ? "INVITE_FAIL" : "INVITE_SUCCESS" });
  }, [contract, inviteUserList, memberContractManageInvite, startWork]);

  // 파일이 업로드될 때 실행되는 함수
  const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileUploadFullModal({ isOpen: false });
    try {
      const file = event.target.files?.[0];

      if (!file) return;

      const reader = new FileReader();
      const data = await new Promise<string | ArrayBuffer>((resolve, reject) => {
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.onerror = (error) => {
          reject(error);
        };
        reader.readAsArrayBuffer(file);
      });

      const workbook = xlsx.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // B열과 C열의 8행부터 데이터가 있는 마지막 행까지 추출
      const contactData = [];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[0-9-+\s()]+$/;
      let hasInvalidData = false;

      // 마지막 데이터 행 아래에 값이 있는지 체크
      const sheetRange = sheet["!ref"];
      if (sheetRange) {
        const range = xlsx.utils.decode_range(sheetRange);
        // decode_range 는 0-based 라 +1 해야 엑셀 행 번호가 된다
        const lastUsedRow = range.e.r + 1;

        if (lastUsedRow > EXCEL_LAST_DATA_ROW) {
          setAlertModal({ isOpen: true, type: "OVER_ROW" });
          return;
        }
      }

      for (let rowIndex = EXCEL_FIRST_DATA_ROW; rowIndex <= EXCEL_LAST_DATA_ROW; rowIndex++) {
        const bCell = sheet[`B${rowIndex}`];
        const cCell = sheet[`C${rowIndex}`];
        const bCellValue = bCell?.v ? String(bCell.v).trim().toLowerCase() : "";
        const cCellValue = cCell?.v ? String(cCell.v).trim() : "";

        // B열과 C열이 모두 빈 값인 경우 다음 행으로 넘어가기
        if (!bCellValue && !cCellValue) {
          continue;
        }

        // C열에 값이 있는 경우에만 B열 체크
        // B열 값이 "phone" 또는 "email"이 아닌 경우
        if (cCellValue && bCellValue !== "phone" && bCellValue !== "email") {
          hasInvalidData = true;
          break; // B열이 잘못되어 있으면 즉시 종료
        }

        // B열 값에 따라 C열 데이터 처리
        const trimmedCellValue = cCellValue.replace(/\s/g, "");

        if (bCellValue === "email") {
          // B열이 "email"인 경우 이메일로 저장
          contactData.push({ email: trimmedCellValue });
        } else if (bCellValue === "phone") {
          // B열이 "phone"인 경우 휴대폰으로 저장
          contactData.push({ phoneNumber: trimmedCellValue });
        }
      }

      if (hasInvalidData) {
        setAlertModal({ isOpen: true, type: "CHECK_B_COLUMN" });
        return;
      }
      if (contactData.length === 0) {
        setAlertModal({ isOpen: true, type: "NO_EXCEL_DATA" });
        return;
      }

      replaceInviteUser(contactData);
    } catch (error) {
      // xlsx.read 는 손상된 파일에 throw 한다. console 로만 삼키면 사용자에게 아무 반응이 없다
      console.error(error);
      setAlertModal({ isOpen: true, type: "NO_EXCEL_DATA" });
    } finally {
      // 같은 파일을 다시 선택할 수 있도록 input 값 초기화
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  // validation 통과 후 submit 될때 실행
  const onSubmit = useCallback((data: InviteUserFormData, e?: any) => {
    e.preventDefault();
  }, []);

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any, e?: any) => {
    return false;
  };

  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        onClickCloseButton={() => {
          navigate(-1);
        }}
      />

      <div className="user-invite-page">
        <div className="user-invite-title">
          <h1 className="base-title">{t("inviteUser.title")}</h1>
        </div>
        <div>
          <input ref={fileRef} onChange={onFileUpload} type="file" name="file" accept=".xlsx, .numbers" required hidden />
          <div className="user-invite-excel-wrapper">
            <div className="ml24 mr24 mt20 flex-center-between ">
              <div className="center">
                <h1>{t("inviteUser.title")}</h1>
              </div>
              <div className="flex-center">
                <div className="file-upload-button-container">
                  <ExcelButton
                    title={t("inviteUser.excelUpload")}
                    onClick={() => {
                      // 웹뷰 인 경우
                      // if (window.ReactNativeWebView) {
                      //   const type = "file";
                      //   const data = {
                      //     event: "select",
                      //     multiple: false, // 여러개 파일 선택 가능하게 할지 여부
                      //     mimeTypes: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"], // 선택 허용할 mimeType
                      //   };
                      //   window.ReactNativeWebView.postMessage(JSON.stringify({ type, data }));
                      // } else {

                      // }
                      setFileUploadFullModal({ isOpen: true });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          {fileUploadFullModal.isOpen && (
            <BaseFullModal isOpen={true}>
              <Header headerType="CLOSE" title={""} onClickCloseButton={() => setFileUploadFullModal({ isOpen: false })}></Header>
              <div className="user-invite-excel-full-modal">
                <h1>{t("excelUpload.title")}</h1>
                <ul className="base-list mt10">
                  <li>{t("excelUpload.onlyGuideFile")}</li>
                </ul>
                <h2 className="mt40 mb20">{t("excelUpload.step1")}</h2>
                <div className="flex-center">
                  <p className="guide-file">{t("excelUpload.guideFile")}</p>
                  <button
                    className="download-btn base-btn size-small color-white"
                    onClick={() => {
                      //파일 다운로드
                      const link = document.createElement("a");
                      link.href = `${process.env.REACT_APP_S3_CONTENT_BASEURL}court/excel/user_bulk_upload_template.xlsx`;
                      link.click();
                    }}
                  >
                    <span>{t("excelUpload.download")}</span>
                  </button>
                </div>
                <h2 className="mt20 mb20">{t("excelUpload.step2")}</h2>
                <div className="flex-center">
                  <p className="guide-file-2 required">{t("excelUpload.attachment")} (0/1)</p>
                  <button className="upload-btn base-btn size-small color-white" onClick={() => fileRef.current?.click()}>
                    <span>{t("excelUpload.browseFile")}</span>
                  </button>
                </div>
              </div>
            </BaseFullModal>
          )}
        </div>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <article className="user-invite-add with-backward-floating">
            <div className="title-wrap">
              <h1>
                <Trans t={t} i18nKey="inviteUser.enterUserInfo" components={{ br: <br /> }} />
              </h1>
            </div>
            <div className="type-wrap">
              <p className="required">{t("inviteUser.type")}</p>
              <div className="flex-center ">
                <BaseRadio
                  checked={selectedType === "1"}
                  id="1"
                  name="type"
                  value="1"
                  label={t("inviteUser.mobileNumber")}
                  className="mr20"
                  onChange={() => {
                    setSelectedType("1");
                    resetField("email");
                  }}
                />
                <BaseRadio
                  checked={selectedType === "2"}
                  id="2"
                  name="type"
                  value="2"
                  label={t("inviteUser.emailAddress")}
                  onChange={() => {
                    setSelectedType("2");
                    resetField("phoneNumber");
                  }}
                />
              </div>
            </div>
            <Controller
              control={control}
              name={selectedType === "1" ? "phoneNumber" : "email"}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <>
                  <BaseTextInputWithButton
                    type={selectedType === "1" ? "phone" : "text"}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={selectedType === "1" ? t("inviteUser.enterMobileNumber") : t("inviteUser.enterEmailAddress")}
                    maxLength={selectedType === "1" ? 20 : 100}
                    actionButtonTitle={t("inviteUser.add")}
                    onClickActionButton={() => {
                      if (selectedType === "1") {
                        const convertedValue = format(value, "KR", "NATIONAL");
                        if (!inviteUserList.find((inviteUser) => inviteUser.phoneNumber === convertedValue)) {
                          appendInviteUser({ phoneNumber: convertedValue });
                          resetField("phoneNumber");
                        } else {
                          setError("phoneNumber", { message: t("inviteUser.duplicatePhone") });
                        }
                      } else {
                        if (!inviteUserList.find((inviteUser) => inviteUser.email === value)) {
                          appendInviteUser({ email: value });
                          resetField("email");
                        } else {
                          setError("email", { message: t("inviteUser.duplicateEmail") });
                        }
                      }
                    }}
                    disabled={isLoadingInviteUsers}
                    disabledActionButton={!value || !!error || isLoadingInviteUsers}
                    isInvalid={!!error}
                    invalidMessage={error?.message}
                  />
                  <div className={!!error ? "mt40" : ""}></div>
                </>
              )}
            />
            {inviteUserList.length > 0 && (
              <div className="user-count-index">
                <p>{t("inviteUser.userCountLabel", { count: inviteUserList.length })}</p>
                <button className="only-text-btn font16" type="button" onClick={() => replaceInviteUser([])}>
                  {t("inviteUser.deleteAll")}
                </button>
              </div>
            )}
            {inviteUserList.map((inviteUser, index: number) => (
              <div key={inviteUser.id} className={"added-user-line"}>
                <p>{inviteUser.phoneNumber || inviteUser.email}</p>
                <button
                  type="button"
                  className={"clear-btn"}
                  onClick={() => {
                    removeInviteUser(index);
                  }}
                  disabled={isLoadingInviteUsers}
                />
              </div>
            ))}
            <BaseButton
              type="button"
              title={t("inviteUser.invite")}
              className="base-btn mt106"
              onClick={() => {
                clickUserInvite();
              }}
              disabled={inviteUserList.length === 0}
              isLoading={isLoadingInviteUsers}
            />
          </article>
        </form>
        {/* 초대하기 메시지 발송 중 로딩 모달 */}
        <InviteLoadingModal isOpen={inviteLoadingModal.isOpen} current={inviteResults.length} total={inviteUserList.length} />
        {/* 초대하기 클릭시 띄우는 컨펌 모달 */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("common:confirm")}
          onClose={() => setConfirmModal({ isOpen: false })}
          onClick={() => clickModalConfirm()}
          className="pa20 pt30"
        >
          <div>
            <p className="font18 font-weight-600">{t("inviteUser.inviteQuestion")}</p>
            <p className="font15 font-weight-400 mt20">{t("inviteUser.inviteAppInstallDesc")}</p>
            <p className="font15 font-weight-400 mt5">{t("inviteUser.inviteGrantDesc")}</p>
          </div>
        </ConfirmModal>
        {/* 최대 이용자수 초과 alert */}
        {alertModal.type === "INVITE_FAIL_MAX_COUNT" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setAlertModal({ isOpen: false });
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.maxUserExceeded")}</p>
              <p className="mt20 font15 font-weight-400">{t("inviteUser.maxUserExceededDesc")}</p>
            </div>
          </ConfirmModal>
        )}
        {/* 이용자 초대 중 실패한 초대 있을 경우 alert */}
        {alertModal.type === "INVITE_FAIL" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnLeftTitle={t("common:confirm")}
            btnRightTitle={t("inviteUser.viewFailReason")}
            onClose={() => {
              setInviteResults([]);
              setAlertModal({ isOpen: false });
            }}
            onClick={() => {
              navigate(`/court/mypage/contracts/${contractId}/users/invite/fail`, {
                state: {
                  inviteResults,
                },
              });
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.inviteCompleted")}</p>
              <p className="mt20 font15 font-weight-400">
                {t("inviteUser.inviteSuccessFailCount", {
                  success: inviteResults.filter((r) => r.type === "SUCCESS").length,
                  fail: inviteResults.filter((r) => r.type === "FAIL").length,
                })}
              </p>
            </div>
          </ConfirmModal>
        )}
        {/* 이용자 초대 모두 성공했을 경우 alert */}
        {alertModal.type === "INVITE_SUCCESS" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setInviteResults([]);
              setAlertModal({ isOpen: false });
              navigate(-1);
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.inviteCompleted")}</p>
              <p className="mt20 font15 font-weight-400">
                {t("inviteUser.inviteSuccessCount", {
                  count: inviteResults.filter((r) => r.type === "SUCCESS").length,
                })}
              </p>
            </div>
          </ConfirmModal>
        )}
        {/* C열 데이터 확인 */}
        {alertModal.type === "CHECK_B_COLUMN" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setAlertModal({ isOpen: false });
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.invalidFile")}</p>
              <p className="mt20 font15 font-weight-400">{t("inviteUser.checkBColumn")}</p>
            </div>
          </ConfirmModal>
        )}
        {/* 엑셀 데이터가 없는 경우 alert */}
        {alertModal.type === "NO_EXCEL_DATA" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setAlertModal({ isOpen: false });
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.noExcelData")}</p>
            </div>
          </ConfirmModal>
        )}
        {alertModal.type === "OVER_ROW" && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setAlertModal({ isOpen: false });
            }}
          >
            <div>
              <p className="font18 font-weight-600">{t("inviteUser.overRow", { max: MAX_UPLOAD_ROWS })}</p>
            </div>
          </ConfirmModal>
        )}
      </div>
    </>
  );
};
export default UserInvite;
